import { useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import { useDispatch } from "react-redux";
import { persistor } from "redux/store/store";
import { startLogout } from "redux/slices/logoutSlice";

let logoutInProgress = false;
const LOGOUT_REDIRECT_DELAY_MS = 3500;

/**
 * Centralized logout hook to prevent multiple logout handlers from firing
 * Ensures a single, unified logout flow
 */
export const useLogout = () => {
  const { instance } = useMsal();
  const dispatch = useDispatch();

  const resolveAccountForLogout = useCallback(() => {
    const activeAccount = instance.getActiveAccount();
    if (activeAccount) {
      return activeAccount;
    }

    const accounts = instance.getAllAccounts();
    if (!accounts.length) {
      return null;
    }

    const userId = sessionStorage.getItem("userId");
    if (userId) {
      const matchingAccount = accounts.find((acc) => {
        const claims = acc.idTokenClaims as Record<string, unknown> | undefined;
        const oid = claims?.oid;
        return typeof oid === "string" && oid === userId;
      });

      if (matchingAccount) {
        instance.setActiveAccount(matchingAccount);
        return matchingAccount;
      }
    }

    instance.setActiveAccount(accounts[0]);
    return accounts[0];
  }, [instance]);

  const resolveLogoutHint = useCallback(
    (account: ReturnType<typeof resolveAccountForLogout>) => {
      if (!account) {
        return undefined;
      }

      const claims = account.idTokenClaims as
        Record<string, unknown> | undefined;
      const hintFromClaims =
        claims?.login_hint ??
        claims?.preferred_username ??
        claims?.email ??
        claims?.upn ??
        claims?.unique_name;

      if (typeof hintFromClaims === "string" && hintFromClaims.length > 0) {
        return hintFromClaims;
      }

      return account.username || undefined;
    },
    [],
  );

  const logout = useCallback(async () => {
    if (logoutInProgress) {
      return;
    }

    try {
      logoutInProgress = true;

      const account = resolveAccountForLogout();
      const logoutHint = resolveLogoutHint(account);

      // 1. Reset app state
      dispatch({ type: "RESET_APP" });

      // 2. Signal that logout is starting (prevents redirect interference)
      dispatch(startLogout());

      // 3. Purge Redux persist cache
      await persistor.purge();

      // Keep the logout loading state visible for a short moment.
      await new Promise((resolve) =>
        setTimeout(resolve, LOGOUT_REDIRECT_DELAY_MS),
      );

      // If account is already gone, do not call provider logout again.
      if (!account) {
        window.location.replace("/login");
        return;
      }

      // 4. Clear local session and skip provider sign-out page navigation
      await instance.logoutRedirect({
        account,
        logoutHint,
        postLogoutRedirectUri: "/login",
        onRedirectNavigate: () => false,
      });

      window.location.replace("/login");
    } catch (error) {
      logoutInProgress = false;
      console.error("Logout failed:", error);
    }
  }, [instance, dispatch, resolveAccountForLogout, resolveLogoutHint]);

  return { logout };
};
